import React, { useEffect, useMemo, useRef } from 'react';
import './MotionPaceChart.css';
import {
  buildMotionPacePreview,
  formatSpeed,
  MAX_SPEED_MAX,
  SPEED_FLOOR,
} from '../../lib/motionPace';
import { MOTION_PACE_CHART, MOTION_PACE_CONTROLS } from '../../lib/operationCopy';
import { niceTimeTicks, formatTime } from '../../utils/motionTimeline';
import useVideoScrub from '../../hooks/useVideoScrub';

// The MotionPace chart (#532) — "speed follows motion", drawn from the PERSISTED
// speed curve.
//
// WHAT IT SHOWS, in the order it is stacked:
//   * dimmed MOTION bars, the same `video.motionValues` the trim chart plots,
//     so the reason the speed does what it does is on the same picture;
//   * the teal SPEED curve on its own right-hand axis;
//   * the MAX-SPEED CEILING, a dashed line that follows the slider LIVE.
//
// THE CEILING IS THE ONLY THING THAT MOVES WITH THE SLIDER, and that is the
// point of the control. The curve itself is recomputed server-side on save
// (#528: computed once, persisted, read by everyone), so between a slider commit
// and the Firestore snapshot the drawn curve still belongs to the previous
// setting — `stale` says so on screen rather than presenting it as the answer.
//
// SVG, NOT CANVAS, unlike the trim chart next to it. The trim chart is canvas
// because it repaints a playhead at 60Hz; nothing here animates, and an SVG
// makes the one thing that must be verifiable — where the ceiling line sits for
// a given slider value — a real coordinate a test can read instead of pixels in
// a bitmap jsdom will not rasterize.
//
// The AXIS DOES NOT SCALE TO THE DATA. A per-drag rescale would keep the
// ceiling line in the same place as the slider moved, which is exactly the
// feedback the control exists to give. It steps between a few FIXED bands
// instead — see SPEED_AXIS_RUNGS.

// User units. Matches the approved comp's geometry (tweedia-comps/
// 17-motion-pace.html) so the shipped chart and the signed-off picture are the
// same drawing.
const VIEW_W = 880;
const VIEW_H = 206;
const PLOT_LEFT = 6;
const PLOT_RIGHT = 836;
const PLOT_W = PLOT_RIGHT - PLOT_LEFT;
const PLOT_TOP = 10;
// Motion bars stand on this line; the time ticks live below it.
const BASELINE = 184;
const TICK_BOTTOM = 189;
const TICK_LABEL_Y = 200;
// The right-hand speed axis: 1x at the bottom of the curve's band, the chosen
// axis top at the top. Deliberately above BASELINE — a 1x stretch has to stay
// readable where the motion bars are tallest.
const SPEED_BOTTOM = 172;
const SPEED_TOP = 18;
const AXIS_LABEL_X = 846;

// The band the axis top is chosen from, smallest that fits the chart's content.
//
// WHY A LADDER AND NOT JUST MAX_SPEED_MAX. The band's HEIGHT is fixed at 154
// units whatever the top is, so pinning the top at the cap spends the band on
// range nobody is using: at the 15x cap that is 11 units per 1x, and a project
// on the 1.5x minimum would draw its entire curve inside 5.5 of 154 — a flat
// line on the baseline. Against the old 6x cap the same project got 15.4. The
// rungs give a low-ceiling project its vertical resolution back (1.5x now sits
// on a 3x axis, 25% of the band) without reintroducing a per-drag rescale:
// changes are discrete and rare, and most drags do not cross one at all.
//
// THE COST, so it is not a surprise in the browser: crossing a rung mid-drag
// rescales the whole plot in one step, and at that instant the ceiling line
// jumps DOWNWARD even though the number went up (at 6x it is at the top of the
// band; at 6.1x the axis becomes 10x and the line drops to 57% of it). That is
// the trade the fixed-height band forces — the alternative is a curve nobody
// can read at the low end. Rungs are kept few and far apart so it is rare.
export const SPEED_AXIS_RUNGS = [3, 6, 10, MAX_SPEED_MAX];

// The smallest rung that contains `ceiling`. A non-finite or over-cap value
// lands on the top rung rather than throwing the axis away.
export const axisTopFor = (ceiling) => (
  SPEED_AXIS_RUNGS.find((rung) => Number.isFinite(ceiling) && ceiling <= rung + 1e-9)
  ?? SPEED_AXIS_RUNGS[SPEED_AXIS_RUNGS.length - 1]
);

const speedToY = (speed, axisTop) => {
  const span = axisTop - SPEED_FLOOR;
  const clamped = Math.max(SPEED_FLOOR, Math.min(axisTop, speed));
  return SPEED_BOTTOM - ((clamped - SPEED_FLOOR) / span) * (SPEED_BOTTOM - SPEED_TOP);
};

// A point's x, as a fraction of the source. Both series carry their own length
// (motion is one score per source PAIR, speed one per source BLOCK), so each
// maps its own index rather than sharing a step.
const fractionToX = (fraction) => PLOT_LEFT + fraction * PLOT_W;

const round2 = (n) => Math.round(n * 100) / 100;

// The speed gridlines that fit on the axis, walked up from the floor. 1x is the
// curve's own baseline: it gets an axis label but no rule.
//
// THE STEP WIDENS ON A WIDE AXIS, because the band does not. Its height is a
// fixed 154px whatever the axis top is, so one gridline per 1x is only readable
// while the range is narrow: on the 15x rung that would be 15 labels 11px apart
// at the 12px font the design uses, i.e. a solid column of text. Stepping by 2
// past a span of 8 keeps them ~22px apart at the widest rung and changes
// nothing at the narrow ones — the 3x and 6x rungs still rule every integer.
// Exported so the rule is testable at tops other than the ones shipping.
export const speedTicks = (floor, ceiling) => {
  const span = ceiling - floor;
  if (!(span > 0)) return [floor];
  const step = span > 8 ? 2 : 1;
  const ticks = [];
  // The epsilon is for a tick landing exactly ON the ceiling, which both of the
  // bounds this has shipped with do (1..6 by 1, and 1..15 by 2).
  for (let s = floor; s <= ceiling + 1e-9; s += step) ticks.push(s);
  return ticks;
};

const polylinePoints = (values, axisTop) => {
  const n = values.length;
  if (n === 0) return '';
  return values
    .map((v, i) => {
      const x = fractionToX(n > 1 ? i / (n - 1) : 0.5);
      return `${round2(x)},${round2(speedToY(v, axisTop))}`;
    })
    .join(' ');
};

// ---------------------------------------------------------------------------
// The time axis, in both directions. SOURCE seconds, the same domain the ticks
// above are drawn from and the same one the editor's <video> is playing, so a
// click maps to `currentTime` with no speed curve to invert.

const timeToPlotX = (t, domainSeconds) => {
  if (!(domainSeconds > 0)) return PLOT_LEFT;
  const fraction = Number.isFinite(t) ? t / domainSeconds : 0;
  return PLOT_LEFT + Math.max(0, Math.min(1, fraction)) * PLOT_W;
};

// Clamped to the PLOT BAND, not to the viewBox: the right-hand speed labels
// live outside PLOT_RIGHT, and a click over them should mean "the end" rather
// than a time past the footage.
export const plotXToTime = (x, domainSeconds) => {
  if (!(domainSeconds > 0)) return 0;
  const px = Number.isFinite(x) ? x : PLOT_LEFT;
  const clamped = Math.max(PLOT_LEFT, Math.min(PLOT_RIGHT, px));
  return ((clamped - PLOT_LEFT) / PLOT_W) * domainSeconds;
};

// One vertical stroke per motion point, from the baseline up. `max` scales the
// band; a dead-flat clip (max 0) draws no bars rather than a full-height wall.
const motionBarPath = (values, max) => {
  const n = values.length;
  if (n === 0 || !(max > 0)) return '';
  const height = BASELINE - PLOT_TOP;
  return values
    .map((v, i) => {
      const x = round2(fractionToX((i + 0.5) / n));
      const top = round2(BASELINE - Math.max(0, Math.min(1, v / max)) * height);
      return `M${x} ${top}V${BASELINE}`;
    })
    .join('');
};

const MotionPaceChart = ({
  // `project.video.speedCurve` — the record #528 persists. Never recomputed.
  speedCurve,
  // `project.video.motionValues`, for the context bars. Optional.
  motionValues,
  // `project.video.captureGapFrames`. The pair spanning each camera-off stretch
  // is zeroed out of the bars, matching what the server masked before computing
  // the curve — see maskCaptureGapPairs.
  captureGapFrames,
  // The LIVE slider value, which is what the ceiling line follows. Falls back to
  // the curve's own ceiling so a chart rendered without a control still draws a
  // truthful line.
  maxSpeed,
  // True while the settings save that recomputes the curve is in flight.
  isRecalculating = false,
  // False when the drawn curve was computed for different settings than the
  // controls now show.
  stale = false,
  // The editor's <video> — the SOURCE footage, same element MotionTrim's chart
  // seeks. Absent (a chart rendered outside the editor) leaves the chart inert:
  // no playhead, no pointer handlers, no keyboard target.
  videoRef,
  // The player's source. Two jobs: re-attach the playback listeners when the
  // player swaps elements (VideoStage keys the <video> by src, so the element's
  // identity can change without this component unmounting), and gate
  // interactivity — the editor only mounts the player once this resolves, so a
  // falsy value means `videoRef.current` is still null.
  videoUrl,
}) => {
  const preview = useMemo(
    () => buildMotionPacePreview(speedCurve, motionValues, { captureGapFrames }),
    [speedCurve, motionValues, captureGapFrames],
  );

  // SEEKING AND THE PLAYHEAD (#575). The chart is a picture of the same footage
  // the player above it is showing, so it doubles as a scrubber — same gesture,
  // same keys and the same seek machinery as the trim chart next door
  // (`useVideoScrub`), so switching modes does not change how the timeline is
  // driven.
  //
  // THE PLAYHEAD IS MOVED BY setAttribute, NOT BY STATE. It follows playback at
  // frame rate; routing that through React would re-render the whole chart 60
  // times a second and rebuild every bar and curve point. Its x is still a real
  // SVG attribute, so it stays as testable as the rest of this drawing.
  const plotRef = useRef(null);
  const playheadLineRef = useRef(null);
  const playheadDotRef = useRef(null);
  const updatePlayheadRef = useRef(null);
  const rafRef = useRef(0);
  const playingRef = useRef(false);

  // Computed before the early return below so the hooks can use it: the time
  // axis exists only when the record carries an fps.
  const domain = preview.mode === 'ready'
    && Number.isFinite(preview.sourceSeconds) && preview.sourceSeconds > 0
    ? preview.sourceSeconds
    : 0;
  // `videoUrl` and not `videoRef.current`: the ref object is passed down from
  // the first render and stays non-null forever, so testing it alone would
  // advertise role="slider" and draw a playhead during the window where the
  // signed URL is still resolving and no <video> is mounted yet — a control
  // that looks live and seeks nothing.
  const interactive = !!(videoRef && videoUrl && domain > 0);

  const scrub = useVideoScrub(videoRef, {
    domain,
    onUpdate: () => {
      if (updatePlayheadRef.current) updatePlayheadRef.current();
    },
  });

  const updatePlayhead = () => {
    const displayTime = scrub.getDisplayTime();
    const x = round2(timeToPlotX(displayTime, domain));
    const line = playheadLineRef.current;
    if (line) {
      line.setAttribute('x1', x);
      line.setAttribute('x2', x);
    }
    const dot = playheadDotRef.current;
    if (dot) dot.setAttribute('cx', x);
    const plot = plotRef.current;
    if (plot && interactive) {
      plot.setAttribute('aria-valuenow', String(Math.round(displayTime)));
      plot.setAttribute(
        'aria-valuetext',
        `${formatTime(displayTime)} of ${formatTime(domain)}`,
      );
    }
  };
  updatePlayheadRef.current = updatePlayhead;

  // Re-apply the imperatively written playhead x and aria after every commit,
  // so they survive a render that rewrites those nodes from JSX.
  useEffect(() => {
    if (updatePlayheadRef.current) updatePlayheadRef.current();
  });

  // Playback wiring. `timeupdate` fires about four times a second — enough for
  // a paused/seeking chart but visibly steppy during playback — so a rAF loop
  // takes over while playing and is cancelled the moment it stops.
  useEffect(() => {
    const update = () => {
      if (updatePlayheadRef.current) updatePlayheadRef.current();
    };
    const video = videoRef && videoRef.current;
    if (!video) {
      update();
      return undefined;
    }

    const tick = () => {
      update();
      if (playingRef.current) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    const onPlay = () => {
      playingRef.current = true;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };
    const onStop = () => {
      playingRef.current = false;
      cancelAnimationFrame(rafRef.current);
      update();
    };
    const onTime = () => update();
    const onSeeked = () => {
      // seek-when-ready: flush whatever the drag queued while the video was busy.
      scrub.onSeeked();
      update();
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onStop);
    video.addEventListener('ended', onStop);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('loadedmetadata', onTime);

    // The chart can mount mid-playback (expanding the Motion card while the
    // preview above is already playing), and the `play` event fired long ago —
    // so seed the loop from the element's state instead of waiting for it.
    playingRef.current = !video.paused && !video.ended;
    if (playingRef.current) {
      rafRef.current = requestAnimationFrame(tick);
    }

    update();

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onStop);
      video.removeEventListener('ended', onStop);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('loadedmetadata', onTime);
      cancelAnimationFrame(rafRef.current);
      playingRef.current = false;
    };
    // `scrub` is referentially stable, so it cannot go out of date here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, videoUrl]);

  if (preview.mode !== 'ready') {
    // THE FIRST SWITCH INTO MOTIONPACE LANDS HERE, NOT ON THE OVERLAY BELOW.
    // Selecting the mode persists immediately and the curve is computed
    // server-side, so for that whole round trip there is no curve to draw and
    // this branch is all the user sees. Showing `pending` — which asks them to
    // move a control to trigger the save — would be telling them to do the thing
    // the app is already doing, so the spinner has to live here too.
    return (
      <div className="mpc" data-testid="motion-pace-chart" data-mode="unavailable">
        {isRecalculating ? (
          <p
            className="mpc__empty mpc__empty--busy"
            role="status"
            aria-live="polite"
            data-testid="motion-pace-chart-recalculating"
          >
            <span className="mpc__spinner" />
            {MOTION_PACE_CHART.recalculating}
          </p>
        ) : (
          <p className="mpc__empty" data-testid="motion-pace-chart-empty">
            {MOTION_PACE_CHART.pending}
          </p>
        )}
      </div>
    );
  }

  const {
    speedPoints, motionPoints, motionMax,
    speedMean, speedMax, outputSeconds, curveMaxSpeed,
  } = preview;

  const ceiling = Number.isFinite(maxSpeed) ? maxSpeed : curveMaxSpeed;

  // The axis has to contain EVERYTHING drawn, not just the slider. Between a
  // slider commit and the Firestore snapshot the chart still shows the previous
  // curve, which may peak well above the new ceiling — sizing the axis to the
  // slider alone would clip that curve into a flat line along the top and
  // present it as the answer.
  const axisTop = axisTopFor(Math.max(
    ...[ceiling, curveMaxSpeed, speedMax].filter((v) => Number.isFinite(v)),
    SPEED_FLOOR,
  ));
  const speedTickValues = speedTicks(SPEED_FLOOR, axisTop);
  const ceilingY = Number.isFinite(ceiling) ? speedToY(ceiling, axisTop) : null;

  const curve = polylinePoints(speedPoints, axisTop);
  const bars = motionPoints ? motionBarPath(motionPoints, motionMax) : '';

  // Time ticks in SOURCE seconds — `domain`, computed above the early return.
  // That is the axis the source <video> and the trim chart already use, so
  // switching modes does not move the timeline under the user. A record with no
  // fps has no time axis at all; the chart still draws, it just carries no
  // clock — and with no clock there is nothing to seek to, so it stays inert.
  const ticks = domain > 0 ? niceTimeTicks(domain, 10) : [];

  const meanLabel = Number.isFinite(speedMean) ? formatSpeed(speedMean) : null;
  const peakLabel = Number.isFinite(speedMax) ? formatSpeed(speedMax) : null;

  // clientX -> viewBox x. The svg fills its box at a preserved aspect ratio
  // (width: 100%, height: auto in MotionPaceChart.css), so ONE linear scale
  // converts the whole way — no getScreenCTM, which jsdom cannot provide and
  // which would make the mapping untestable.
  const clientXToTime = (clientX) => {
    const el = plotRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    if (!rect || !(rect.width > 0)) return 0;
    return plotXToTime((clientX - rect.left) * (VIEW_W / rect.width), domain);
  };

  const onPointerDown = (e) => {
    if (!interactive) return;
    const el = plotRef.current;
    if (!el) return;
    // Capture, so a drag that leaves the chart keeps scrubbing it.
    try {
      el.setPointerCapture(e.pointerId);
    } catch (err) {
      // ignore
    }
    scrub.beginScrub();
    scrub.seekTo(clientXToTime(e.clientX));
  };

  const onPointerMove = (e) => {
    if (!scrub.isScrubbing()) return;
    scrub.seekTo(clientXToTime(e.clientX));
  };

  const endScrub = (e) => {
    if (!scrub.isScrubbing()) return;
    const el = plotRef.current;
    if (el) {
      try {
        el.releasePointerCapture(e.pointerId);
      } catch (err) {
        // ignore
      }
    }
    // Clears the scrub flag before the final seek, so the release lands as one
    // precise currentTime write rather than another fastSeek.
    scrub.endScrub(clientXToTime(e.clientX));
    updatePlayhead();
  };

  const onKeyDown = (e) => {
    if (!interactive) return;
    const cur = scrub.getStepBaseTime();
    const step = e.shiftKey ? 5 : 1;
    let target = null;
    switch (e.key) {
      case 'ArrowLeft':
        target = cur - step;
        break;
      case 'ArrowRight':
        target = cur + step;
        break;
      case 'Home':
        target = 0;
        break;
      case 'End':
        target = domain;
        break;
      default:
        return;
    }
    e.preventDefault();
    scrub.seekTo(target);
    updatePlayhead();
  };

  // The slider role goes on the WRAPPER, matching MotionTimeline: the svg keeps
  // role="img" and its description of the drawing, and the wrapper carries the
  // position. Absent a player the wrapper is a plain div — the chart is exactly
  // as inert as it was before this existed.
  const plotProps = interactive ? {
    role: 'slider',
    tabIndex: 0,
    'aria-label': 'Video timeline',
    'aria-valuemin': 0,
    'aria-valuemax': Math.round(domain),
    'aria-valuenow': 0,
    'aria-valuetext': `0:00 of ${formatTime(domain)}`,
    onPointerDown,
    onPointerMove,
    onPointerUp: endScrub,
    onPointerCancel: endScrub,
    onKeyDown,
  } : {};

  return (
    <div
      className="mpc"
      data-testid="motion-pace-chart"
      data-mode="ready"
      data-stale={String(!!stale)}
    >
      <div className="mpc__head">
        <b className="mpc__title">{MOTION_PACE_CHART.title}</b>
        {meanLabel && peakLabel && (
          <span className="mpc__summary" data-testid="motion-pace-chart-summary">
            nothing is cut &mdash; average <span className="mpc__hi">{meanLabel}x</span>
            {' '}&middot; peak <span className="mpc__hi">{peakLabel}x</span>
          </span>
        )}
      </div>
      <div
        className="mpc__plot"
        data-testid="motion-pace-chart-plot"
        ref={plotRef}
        {...plotProps}
      >
        <svg
          className="mpc__svg"
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          role="img"
          data-testid="motion-pace-chart-svg"
          aria-label={[
            'Playback speed across this shoot',
            meanLabel ? `averaging ${meanLabel} times source speed` : null,
            peakLabel ? `peaking at ${peakLabel} times` : null,
            Number.isFinite(ceiling) ? `with a ${formatSpeed(ceiling)} times ceiling` : null,
            Number.isFinite(outputSeconds)
              ? `The whole shoot plays in ${formatTime(outputSeconds)} without cutting anything.`
              : null,
          ].filter(Boolean).join(', ')}
        >
          <g className="mpc__grid" aria-hidden="true">
            {speedTickValues.filter((s) => s > SPEED_FLOOR).map((s) => (
              <line
                key={s}
                x1={PLOT_LEFT}
                y1={speedToY(s, axisTop)}
                x2={PLOT_RIGHT}
                y2={speedToY(s, axisTop)}
              />
            ))}
          </g>

          {bars && (
            <path
              className="mpc__bars"
              d={bars}
              data-testid="motion-pace-chart-bars"
              strokeWidth={Math.max(1, round2((PLOT_W / motionPoints.length) * 0.6))}
            />
          )}

          {ceilingY !== null && (
            <>
              <line
                className="mpc__ceiling"
                x1={PLOT_LEFT}
                y1={round2(ceilingY)}
                x2={PLOT_RIGHT}
                y2={round2(ceilingY)}
                data-testid="motion-pace-chart-ceiling"
              />
              <text
                className="mpc__ceiling-label"
                x={PLOT_LEFT + 4}
                y={round2(ceilingY) - 4}
                data-testid="motion-pace-chart-ceiling-label"
              >
                {MOTION_PACE_CONTROLS.maxSpeed.label.toLowerCase()} {formatSpeed(ceiling)}x
              </text>
            </>
          )}

          {/* Halo under the stroke, so the curve stays readable where it crosses
              the tallest motion bars. */}
          <polyline className="mpc__curve-halo" points={curve} aria-hidden="true" />
          <polyline
            className="mpc__curve"
            points={curve}
            data-testid="motion-pace-chart-curve"
          />

          <g className="mpc__axis" aria-hidden="true">
            {speedTickValues.map((s) => (
              <text key={s} x={AXIS_LABEL_X} y={round2(speedToY(s, axisTop)) + 4}>
                {formatSpeed(s)}x
              </text>
            ))}
          </g>

          <g className="mpc__ticks" aria-hidden="true">
            {ticks.map((t) => {
              const x = round2(fractionToX(t / domain));
              return <line key={t} x1={x} y1={BASELINE} x2={x} y2={TICK_BOTTOM} />;
            })}
          </g>
          <g className="mpc__tick-labels" aria-hidden="true">
            {ticks.map((t, i) => (
              <text
                key={t}
                x={round2(fractionToX(t / domain))}
                y={TICK_LABEL_Y}
                textAnchor={i === 0 ? 'start' : 'middle'}
              >
                {formatTime(t)}
              </text>
            ))}
          </g>

          {/* THE PLAYHEAD IS DRAWN LAST so it sits over the curve and the bars.
              Same red, same weight and the same grab dot as the trim chart's,
              because it is the same playhead on the same footage — the two
              charts have to read as one instrument in two modes. Its x is
              written by setAttribute from here on; the values below are only the
              position it mounts at. */}
          {interactive && (
            <g className="mpc__playhead" aria-hidden="true">
              <line
                ref={playheadLineRef}
                data-testid="motion-pace-chart-playhead"
                x1={PLOT_LEFT}
                x2={PLOT_LEFT}
                y1={PLOT_TOP}
                y2={BASELINE}
              />
              <circle
                ref={playheadDotRef}
                cx={PLOT_LEFT}
                cy={PLOT_TOP}
                r={5}
              />
            </g>
          )}
        </svg>
      </div>

      <div className="mpc__legend" data-testid="motion-pace-chart-legend">
        <span className="mpc__legend-item">
          <i className="mpc__swatch mpc__swatch--motion" />
          {MOTION_PACE_CHART.motionLabel}
        </span>
        <span className="mpc__legend-item">
          <i className="mpc__swatch mpc__swatch--speed" />
          {MOTION_PACE_CHART.speedLabel}
        </span>
        <span className="mpc__legend-item">
          <i className="mpc__swatch mpc__swatch--ceiling" />
          {MOTION_PACE_CHART.ceilingLabel}
        </span>
      </div>

      {/* THE OVERLAY IS FOR THE ROUND TRIP, NOT FOR THE MISMATCH. `stale` is
          true from the first pixel of a drag — dimming the chart behind a
          spinner then would cover the ceiling line at the exact moment it is
          the only feedback the slider gives. So a stale-but-idle chart only
          fades the line that is out of date (see the [data-stale] rule in
          MotionPaceChart.css), and the overlay waits for a real recompute. */}
      {isRecalculating && (
        <div className="mpc__overlay" role="status" aria-live="polite">
          <span className="mpc__spinner" />
          <span className="mpc__overlay-text">{MOTION_PACE_CHART.recalculating}</span>
        </div>
      )}
    </div>
  );
};

export default MotionPaceChart;
